import type {
  CSSProperties,
  ReactNode,
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  Ref,
} from 'react';
import { useId } from 'react';
import { color, font, fontSize, fontWeight, radius, shadow, layout, motion } from './tokens';

/**
 * The MedOS primitive set.
 *
 * Feature screens compose these; they do not write hex values or re-derive
 * paddings. Two consequences worth knowing before you reach past them:
 *
 * - Every interactive element here meets the 44px touch target and carries a
 *   visible focus ring. Hand-rolling a `<div onClick>` loses both, and both are
 *   WCAG 2.2 AA obligations (PRD 21.6).
 * - Disabled controls take a `disabledReason`. PRD 7.4 requires that a control
 *   disabled by permission or workflow state says why, so the prop is not
 *   optional decoration - a bare `disabled` renders a control the user cannot
 *   explain.
 */

// ---------------------------------------------------------------------------
// Text
// ---------------------------------------------------------------------------

/**
 * Latin-locked text: MRNs, invoice numbers, phone numbers, times, lab values,
 * money.
 *
 * `dir="ltr"` is the point. Inside an Arabic (RTL) paragraph the bidi algorithm
 * will otherwise reorder a string like "MRN-100482" or "6.9 mmol/L" and the
 * clinician reads the wrong value. This is a patient-safety detail, not a
 * typographic one.
 */
export function Mono({
  children,
  size = fontSize.base,
  weight = fontWeight.regular,
  style,
}: {
  children: ReactNode;
  size?: string;
  weight?: number;
  style?: CSSProperties;
}) {
  return (
    <span
      dir="ltr"
      style={{
        fontFamily: font.mono,
        fontSize: size,
        fontWeight: weight,
        unicodeBidi: 'isolate',
        ...style,
      }}
    >
      {children}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Button
// ---------------------------------------------------------------------------

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

const BUTTON_SIZE: Record<ButtonSize, CSSProperties> = {
  sm: { padding: '7px 12px', fontSize: fontSize.sm, minHeight: '32px' },
  md: { padding: '10px 16px', fontSize: '13.5px', minHeight: '38px' },
  lg: { padding: '12px 18px', fontSize: fontSize.lg, minHeight: layout.minTouchTarget },
};

const BUTTON_VARIANT: Record<ButtonVariant, CSSProperties> = {
  primary: {
    background: color.brand.primary,
    color: color.text.onDark,
    border: 'none',
    fontWeight: fontWeight.bold,
  },
  secondary: {
    background: color.surface.card,
    color: color.text.primary,
    border: `1px solid ${color.border.default}`,
    fontWeight: fontWeight.medium,
  },
  ghost: {
    background: 'transparent',
    color: color.brand.primary,
    border: 'none',
    fontWeight: fontWeight.bold,
  },
  danger: {
    background: color.status.danger.fg,
    color: color.text.onDark,
    border: 'none',
    fontWeight: fontWeight.bold,
  },
};

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'title'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  /**
   * Why the control is unavailable. Rendered as the accessible name and the
   * tooltip. Required whenever `disabled` is true and the reason is not
   * self-evident from the screen - permission and workflow-state blocks always
   * qualify.
   */
  disabledReason?: string;
  fullWidth?: boolean;
}

export function Button({
  variant = 'secondary',
  size = 'md',
  loading = false,
  disabled = false,
  disabledReason,
  fullWidth = false,
  children,
  style,
  ...rest
}: ButtonProps) {
  const isDisabled = disabled || loading;
  return (
    <button
      type="button"
      {...rest}
      disabled={isDisabled}
      // aria-disabled as well as disabled: a `disabled` button is removed from
      // the tab order, so a screen-reader user never reaches the explanation.
      aria-disabled={isDisabled}
      title={isDisabled ? disabledReason : undefined}
      aria-describedby={undefined}
      style={{
        fontFamily: 'inherit',
        borderRadius: radius.md,
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        opacity: isDisabled ? 0.55 : 1,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '7px',
        whiteSpace: 'nowrap',
        transition: `background ${motion.fast} ${motion.easing}`,
        width: fullWidth ? '100%' : undefined,
        boxSizing: 'border-box',
        ...BUTTON_SIZE[size],
        ...BUTTON_VARIANT[variant],
        ...style,
      }}
    >
      {loading && <Spinner size={13} tone={variant === 'secondary' ? 'dark' : 'light'} />}
      {children}
      {isDisabled && disabledReason ? <span className="sr-only">{disabledReason}</span> : null}
    </button>
  );
}

export function Spinner({ size = 16, tone = 'dark' }: { size?: number; tone?: 'dark' | 'light' }) {
  return (
    <span
      role="status"
      aria-live="polite"
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        border: `2px solid ${tone === 'dark' ? color.border.strong : 'rgba(255,255,255,.35)'}`,
        borderTopColor: tone === 'dark' ? color.brand.primary : '#fff',
        display: 'inline-block',
        animation: 'medos-spin .7s linear infinite',
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// Surfaces
// ---------------------------------------------------------------------------

export function Card({
  children,
  padding = '0',
  style,
}: {
  children: ReactNode;
  padding?: string;
  style?: CSSProperties;
}) {
  return (
    <div
      style={{
        background: color.surface.card,
        border: `1px solid ${color.border.default}`,
        borderRadius: radius.xl,
        overflow: 'hidden',
        padding,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

export function CardHeader({
  title,
  subtitle,
  action,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div
      style={{
        padding: '15px 20px',
        borderBottom: `1px solid ${color.border.subtle}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
      }}
    >
      <div>
        <div style={{ fontSize: fontSize.lg, fontWeight: fontWeight.black }}>{title}</div>
        {subtitle ? (
          <div style={{ fontSize: '12.5px', color: color.text.muted, marginTop: '3px' }}>
            {subtitle}
          </div>
        ) : null}
      </div>
      {action}
    </div>
  );
}

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        marginBottom: '20px',
        gap: '20px',
        flexWrap: 'wrap',
      }}
    >
      <div>
        <h1 style={{ fontSize: fontSize['5xl'], fontWeight: fontWeight.black, margin: 0 }}>
          {title}
        </h1>
        {subtitle ? (
          <div style={{ fontSize: fontSize.md, color: color.text.muted, marginTop: '4px' }}>
            {subtitle}
          </div>
        ) : null}
      </div>
      {actions ? <div style={{ display: 'flex', gap: '9px' }}>{actions}</div> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Status
// ---------------------------------------------------------------------------

export type StatusTone =
  | 'success'
  | 'info'
  | 'warning'
  | 'danger'
  | 'neutral'
  | 'checkout'
  | 'brand';

const TONE: Record<StatusTone, { fg: string; bg: string }> = {
  success: { fg: color.status.success.fg, bg: color.status.success.bg },
  info: { fg: color.status.info.fg, bg: color.status.info.bg },
  warning: { fg: color.status.warning.fg, bg: color.status.warning.bg },
  danger: { fg: color.status.danger.fg, bg: color.status.dangerChip },
  neutral: { fg: color.status.neutral.fg, bg: color.status.neutral.bg },
  checkout: { fg: color.status.checkout.fg, bg: color.status.checkout.bg },
  brand: { fg: color.brand.primary, bg: color.brand.soft },
};

/**
 * A state chip.
 *
 * `icon` is not decorative: PRD 7.3 forbids colour as the only indicator, and
 * a badge that renders in monochrome print (a prescription, an invoice) has
 * nothing but the glyph and the label left. Pass one for anything clinical.
 */
export function StatusBadge({
  tone = 'neutral',
  icon,
  children,
}: {
  tone?: StatusTone;
  icon?: ReactNode;
  children: ReactNode;
}) {
  const t = TONE[tone];
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '5px',
        padding: '3px 10px',
        borderRadius: radius.pill,
        background: t.bg,
        color: t.fg,
        fontSize: '11.5px',
        fontWeight: fontWeight.bold,
        whiteSpace: 'nowrap',
      }}
    >
      {icon ? <span aria-hidden>{icon}</span> : null}
      {children}
    </span>
  );
}

/** A KPI tile. Numbers are Latin-locked so 47 never renders as ٤٧ reversed. */
export function KpiTile({
  label,
  value,
  tone,
  hint,
}: {
  label: ReactNode;
  value: ReactNode;
  tone?: StatusTone;
  hint?: ReactNode;
}) {
  return (
    <Card style={{ padding: '16px 18px' }}>
      <div
        style={{ fontSize: fontSize.sm, color: color.text.muted, fontWeight: fontWeight.medium }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: '28px',
          fontWeight: fontWeight.black,
          fontFamily: font.mono,
          marginTop: '5px',
          color: tone ? TONE[tone].fg : color.text.primary,
        }}
        dir="ltr"
      >
        {value}
      </div>
      {hint ? (
        <div style={{ fontSize: fontSize.sm, color: color.text.muted, marginTop: '4px' }}>
          {hint}
        </div>
      ) : null}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Forms
// ---------------------------------------------------------------------------

/**
 * Persistent label + control + error.
 *
 * PRD 7.4 bans placeholder-only labels: the label has to survive the field
 * being filled in, and a screen reader has to reach it. `Field` renders a real
 * `<label for>` and wires `aria-describedby` for the error and hint.
 */
export function Field({
  label,
  required = false,
  error,
  hint,
  children,
}: {
  label: ReactNode;
  required?: boolean;
  error?: string;
  hint?: string;
  children: (props: {
    id: string;
    'aria-describedby': string | undefined;
    'aria-invalid': boolean;
  }) => ReactNode;
}) {
  const id = useId();
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;
  const describedBy = [error ? errorId : null, hint ? hintId : null].filter(Boolean).join(' ');

  return (
    <div>
      <label
        htmlFor={id}
        style={{
          display: 'block',
          fontSize: fontSize.base,
          fontWeight: fontWeight.medium,
          color: color.text.secondary,
          marginBottom: '6px',
        }}
      >
        {label}
        {required ? (
          <span style={{ color: color.status.danger.fg }} aria-hidden>
            {' '}
            *
          </span>
        ) : null}
      </label>
      {children({
        id,
        'aria-describedby': describedBy || undefined,
        'aria-invalid': Boolean(error),
      })}
      {hint ? (
        <div
          id={hintId}
          style={{ fontSize: fontSize.sm, color: color.text.muted, marginTop: '5px' }}
        >
          {hint}
        </div>
      ) : null}
      {error ? (
        <div
          id={errorId}
          role="alert"
          style={{
            fontSize: fontSize.sm,
            color: color.status.danger.fg,
            marginTop: '5px',
            fontWeight: fontWeight.medium,
          }}
        >
          {error}
        </div>
      ) : null}
    </div>
  );
}

export interface TextInputProps extends InputHTMLAttributes<HTMLInputElement> {
  /**
   * Latin-locked input: phone numbers, national IDs, MRNs, emails, amounts.
   * Renders in the mono face and forces LTR so digits do not reorder.
   */
  latin?: boolean;
  invalid?: boolean;
  /**
   * React 19 passes `ref` as an ordinary prop, so it reaches the `<input>`
   * through the spread below with no `forwardRef` wrapper. It is declared here
   * because `InputHTMLAttributes` carries attributes only, so without it a
   * caller that needs to move focus - the condition picker, returning the cursor
   * to the box after the selection is cleared - fails to typecheck.
   */
  ref?: Ref<HTMLInputElement>;
}

export function TextInput({ latin = false, invalid = false, style, ...rest }: TextInputProps) {
  return (
    <input
      {...rest}
      // `rest.dir` and not `undefined`: this line sits after the spread, so an
      // explicit `undefined` here would silently drop a `dir` the caller passed.
      // `latin` still wins, because that is what it is for.
      dir={latin ? 'ltr' : rest.dir}
      style={{
        width: '100%',
        boxSizing: 'border-box',
        padding: '11px 13px',
        border: `1px solid ${invalid ? color.status.danger.fg : color.border.default}`,
        borderRadius: radius.md,
        fontFamily: latin ? font.mono : 'inherit',
        fontSize: latin ? '13.5px' : fontSize.md,
        background: rest.readOnly ? color.surface.muted : color.surface.card,
        color: color.text.primary,
        minHeight: layout.minTouchTarget,
        ...style,
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// Table
// ---------------------------------------------------------------------------

export function Table({ children, caption }: { children: ReactNode; caption?: string }) {
  return (
    // The wrapper scrolls, not the page: a wide clinical table must never make
    // the whole document scroll sideways.
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        {caption ? <caption className="sr-only">{caption}</caption> : null}
        {children}
      </table>
    </div>
  );
}

export function Th({
  children,
  align = 'start',
}: {
  children: ReactNode;
  align?: 'start' | 'end' | 'center';
}) {
  return (
    <th
      scope="col"
      style={{
        textAlign: align,
        padding: '11px 18px',
        fontSize: fontSize.sm,
        fontWeight: fontWeight.bold,
        color: color.text.muted,
        borderBottom: `1px solid ${color.border.subtle}`,
        background: color.surface.subtle,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </th>
  );
}

export function Td({
  children,
  align = 'start',
  style,
}: {
  children: ReactNode;
  align?: 'start' | 'end' | 'center';
  style?: CSSProperties;
}) {
  return (
    <td
      style={{
        textAlign: align,
        padding: '12px 18px',
        fontSize: '13.5px',
        borderBottom: `1px solid ${color.border.subtle}`,
        ...style,
      }}
    >
      {children}
    </td>
  );
}

// ---------------------------------------------------------------------------
// Overlays
// ---------------------------------------------------------------------------

/**
 * Right-to-left drawer (opens from the inline-start edge under RTL).
 *
 * Focus management, Escape-to-close and scrim click are handled here so a
 * feature screen cannot ship a dialog that traps a keyboard user - see
 * `useDialogBehaviour`.
 */
export function Drawer({
  open,
  onClose,
  title,
  children,
  footer,
  width = layout.drawerWidth,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  width?: string;
}) {
  if (!open) return null;
  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(15,23,42,.35)',
          zIndex: 80,
        }}
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
        style={{
          position: 'fixed',
          insetBlock: 0,
          insetInlineStart: 0,
          width,
          maxWidth: '100vw',
          background: color.surface.card,
          zIndex: 81,
          boxShadow: shadow.drawer,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <header
          style={{
            padding: '18px 20px',
            borderBottom: `1px solid ${color.border.subtle}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ fontSize: fontSize.xl, fontWeight: fontWeight.black }}>{title}</div>
          <Button variant="ghost" size="sm" onClick={onClose} aria-label="إغلاق">
            ✕
          </Button>
        </header>
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>{children}</div>
        {footer ? (
          <footer
            style={{
              padding: '14px 20px',
              borderTop: `1px solid ${color.border.subtle}`,
              display: 'flex',
              gap: '9px',
              justifyContent: 'flex-end',
            }}
          >
            {footer}
          </footer>
        ) : null}
      </aside>
    </>
  );
}

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  width = '620px',
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  width?: string;
}) {
  if (!open) return null;
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,.45)',
        zIndex: 95,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '80px 16px 40px',
        overflowY: 'auto',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
        style={{
          width,
          maxWidth: '94vw',
          background: color.surface.card,
          borderRadius: radius['2xl'],
          boxShadow: shadow.modal,
          overflow: 'hidden',
        }}
      >
        <header
          style={{
            padding: '18px 24px',
            borderBottom: `1px solid ${color.border.subtle}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ fontSize: fontSize['3xl'], fontWeight: fontWeight.black }}>{title}</div>
          <Button variant="ghost" size="sm" onClick={onClose} aria-label="إغلاق">
            ✕
          </Button>
        </header>
        <div style={{ padding: '20px 24px' }}>{children}</div>
        {footer ? (
          <footer
            style={{
              padding: '14px 24px',
              borderTop: `1px solid ${color.border.subtle}`,
              display: 'flex',
              gap: '9px',
              justifyContent: 'flex-end',
            }}
          >
            {footer}
          </footer>
        ) : null}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Misc
// ---------------------------------------------------------------------------

export function ProgressBar({
  value,
  max = 100,
  tone = 'brand',
  label,
}: {
  value: number;
  max?: number;
  tone?: StatusTone;
  label?: string;
}) {
  const pct = max > 0 ? Math.min(100, Math.max(0, (value / max) * 100)) : 0;
  return (
    <div
      role="progressbar"
      aria-valuenow={value}
      aria-valuemin={0}
      aria-valuemax={max}
      aria-label={label}
      style={{
        height: '8px',
        background: color.border.muted,
        borderRadius: radius.xs,
        overflow: 'hidden',
      }}
    >
      <div style={{ width: `${pct}%`, height: '100%', background: TONE[tone].fg }} />
    </div>
  );
}

export function Divider() {
  return <hr style={{ border: 0, borderTop: `1px solid ${color.border.subtle}`, margin: 0 }} />;
}
